import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiMenu, FiMail, FiTrash2, FiSearch } from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import { getAllMessages, deleteMessage, getMessage } from '../../Redux/slices/messageSlice';

const Messages = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const dispatch = useDispatch();
  const { messages, loading } = useSelector(state => state.messages);

  useEffect(() => {
    dispatch(getAllMessages());
  }, [dispatch]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      await dispatch(deleteMessage(id));
      if (selectedMessage?._id === id) setSelectedMessage(null);
    }
  };

  const handleView = async (message) => {
    setSelectedMessage(message);
    if (message.status === 'unread') {
      await dispatch(getMessage(message._id));
      dispatch(getAllMessages());
    }
  };

  const filteredMessages = messages.filter(msg =>
    msg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unreadCount = messages.filter(msg => msg.status === 'unread').length;

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} currentPage="messages" />

      <div className="lg:ml-64">
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden">
              <FiMenu size={24} />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Contact Messages</h2>
              <p className="text-sm text-gray-600">{unreadCount} unread message{unreadCount !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">Admin User</p>
                <p className="text-xs text-gray-500">admin@houseofpraise.com</p>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                A
              </div>
            </div>
          </div>
        </header>

        <main className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Messages List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-4 mb-4">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search messages..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredMessages.length > 0 ? (
                  <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                    {filteredMessages.map((msg) => (
                      <div
                        key={msg._id}
                        onClick={() => handleView(msg)}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                          msg.status === 'unread' ? 'bg-blue-50' : ''
                        } ${selectedMessage?._id === msg._id ? 'border-l-4 border-blue-600' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-800">{msg.name}</h3>
                          {msg.status === 'unread' && (
                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{msg.email}</p>
                        {msg.subject && <p className="text-sm font-medium text-gray-700 mb-1">{msg.subject}</p>}
                        <p className="text-sm text-gray-500 line-clamp-2">{msg.message}</p>
                        <p className="text-xs text-gray-400 mt-2">{new Date(msg.createdAt).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No messages found</p>
                )}
              </div>
            </div>

            {/* Message Detail */}
            <div className="lg:col-span-2">
              {selectedMessage ? (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedMessage.name}</h2>
                      {selectedMessage.subject && <p className="text-lg font-medium text-gray-700 mb-2">{selectedMessage.subject}</p>}
                      <p className="text-gray-600">{selectedMessage.email}</p>
                      {selectedMessage.phone && <p className="text-gray-600">{selectedMessage.phone}</p>}
                    </div>
                    <button
                      onClick={() => handleDelete(selectedMessage._id)}
                      className="text-red-600 hover:text-red-700 p-2"
                    >
                      <FiTrash2 size={20} />
                    </button>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <p className="text-sm text-gray-500 mb-4">Received on {new Date(selectedMessage.createdAt).toLocaleDateString()}</p>
                    <div className="bg-gray-50 rounded-lg p-6">
                      <p className="text-gray-800 leading-relaxed">{selectedMessage.message}</p>
                    </div>
                  </div>

                  <div className="mt-6 flex gap-3">
                    <a
                      href={`mailto:${selectedMessage.email}`}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg text-center transition"
                    >
                      Reply via Email
                    </a>
                    <a
                      href={`tel:${selectedMessage.phone}`}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg text-center transition"
                    >
                      Call
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-md p-12 text-center">
                  <FiMail className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No Message Selected</h3>
                  <p className="text-gray-600">Select a message from the list to view details</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Messages;
